/**
 * Sparrow's sounds: the "Pop and ring" palette James chose in the playground
 * (docs/mockups/sound-feel.html). Everything is synthesized with Web Audio: a sine that
 * drops into its note (the pop), a click of filtered noise, and two slightly inharmonic
 * partials that keep ringing (the ring). Nothing is a recording, so this stays tiny.
 *
 * - The AudioContext is made lazily, and made (or resumed) on a user gesture: the first
 *   key press or tap (browsers won't start audio before one), and again after the phone
 *   suspends it.
 * - A gentle fixed volume (the playground's 70%), through a compressor so a chord never clips.
 * - A silent no-op when Sounds is off in Settings, when there's no Web Audio (tests, jsdom,
 *   old browsers), or if anything throws. Sound never carries meaning on its own: every
 *   event it marks is also shown and announced.
 * - Reduced motion doesn't silence it (sound isn't motion); the voice is subtle anyway.
 */
import { settings } from '../app/settings.ts';

type Palette = { wave: OscillatorType; drop: number; body: number; click: number; partials: readonly (readonly [number, number, number])[] };

const POP: Palette = { wave: 'sine', drop: 1.9, body: 0.09, click: 0.22, partials: [[2.003, 0.22, 0.38], [3.01, 0.07, 0.22]] };
/** The low wood thud, only for a check that finds mistakes. */
const WOOD: Palette = { wave: 'triangle', drop: 1.25, body: 0.055, click: 0.45, partials: [[2.52, 0.1, 0.07]] };

const VOLUME = 0.7;

interface Audio {
	ctx: AudioContext;
	master: GainNode;
	noise: AudioBuffer;
}
let audio: Audio | null = null;
let unavailable = false;

const enabled = (): boolean => {
	try {
		return settings().get().sound;
	} catch {
		return false;
	}
};

/** The context, made on first use and resumed if the browser suspended it. Null without Web Audio. */
function ac(): Audio | null {
	if (unavailable) return null;
	if (!audio) {
		const C =
			typeof window === 'undefined'
				? undefined
				: (window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
		if (typeof C !== 'function') {
			unavailable = true;
			return null;
		}
		const ctx = new C();
		const master = ctx.createGain();
		master.gain.value = VOLUME;
		const comp = ctx.createDynamicsCompressor();
		comp.threshold.value = -14;
		comp.ratio.value = 4;
		master.connect(comp);
		comp.connect(ctx.destination);
		const noise = ctx.createBuffer(1, Math.round(ctx.sampleRate * 0.05), ctx.sampleRate);
		const d = noise.getChannelData(0);
		for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
		audio = { ctx, master, noise };
	}
	if (audio.ctx.state === 'suspended') void audio.ctx.resume().catch(() => {});
	return audio;
}

/** Wake the audio on a gesture (only when Sounds is on, so a muted player never makes a context). */
function unlock(): void {
	try {
		if (enabled()) ac();
	} catch {
		unavailable = true;
	}
}
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
	for (const type of ['pointerdown', 'keydown'] as const) window.addEventListener(type, unlock, { capture: true, passive: true });
}

interface PopOptions {
	t?: number;
	gain?: number;
	ring?: number;
	pan?: number;
	palette?: Palette;
}

/** One pop and its ring, `t` seconds from now. */
function pop(a: Audio, freq: number, { t = 0, gain = 0.55, ring = 1, pan = 0, palette = POP }: PopOptions = {}): void {
	const c = a.ctx;
	const now = c.currentTime + t + 0.005;
	const out = c.createGain();
	out.gain.value = gain;
	if (typeof c.createStereoPanner === 'function') {
		const p = c.createStereoPanner();
		p.pan.value = pan;
		out.connect(p);
		p.connect(a.master);
	} else out.connect(a.master);

	const o = c.createOscillator();
	o.type = palette.wave;
	o.frequency.setValueAtTime(freq * palette.drop, now);
	o.frequency.exponentialRampToValueAtTime(freq, now + 0.03);
	const g = c.createGain();
	g.gain.setValueAtTime(0.0001, now);
	g.gain.exponentialRampToValueAtTime(0.9, now + 0.003);
	g.gain.exponentialRampToValueAtTime(0.0001, now + palette.body);
	o.connect(g);
	g.connect(out);
	o.start(now);
	o.stop(now + palette.body + 0.05);

	for (const [m, amp, dec] of palette.partials) {
		const r = c.createOscillator();
		r.type = 'sine';
		r.frequency.value = freq * m;
		const rg = c.createGain();
		const end = now + dec * ring + 0.02;
		rg.gain.setValueAtTime(0.0001, now);
		rg.gain.exponentialRampToValueAtTime(amp, now + 0.006);
		rg.gain.exponentialRampToValueAtTime(0.0001, end);
		r.connect(rg);
		rg.connect(out);
		r.start(now);
		r.stop(end + 0.05);
	}

	if (palette.click) {
		const n = c.createBufferSource();
		n.buffer = a.noise;
		const bp = c.createBiquadFilter();
		bp.type = 'bandpass';
		bp.frequency.value = Math.min(freq * 3, 9000);
		bp.Q.value = 1.4;
		const ng = c.createGain();
		ng.gain.setValueAtTime(palette.click, now);
		ng.gain.exponentialRampToValueAtTime(0.0001, now + 0.018);
		n.connect(bp);
		bp.connect(ng);
		ng.connect(out);
		n.start(now);
		n.stop(now + 0.03);
	}
}

const BASE = 523.25; // C5
const PENTA = [0, 2, 4, 7, 9];
/** The i-th note of a major pentatonic climbing from C5 (a letter's pitch by its place in the answer). */
export const step = (i: number): number => BASE * 2 ** ((Math.floor(i / 5) * 12 + PENTA[((i % 5) + 5) % 5]!) / 12);
const semi = (s: number, base = BASE): number => base * 2 ** (s / 12);
const jitter = (): number => 1 + (Math.random() - 0.5) * 0.012;

/** The word chime: a quick major triad on G, its top note ringing. */
function chime(a: Audio): void {
	[0, 4, 7].forEach((s, k) => pop(a, semi(s + 7), { t: k * 0.075, gain: 0.5, ring: k === 2 ? 2.2 : 0.8, pan: (k - 1) * 0.25 }));
}

const EVENTS = {
	/** A letter typed: pitch climbs the pentatonic with the cell's index in the answer. */
	key: (a: Audio, i = 0) => pop(a, step(i) * jitter(), { gain: 0.45, pan: Math.min(0.4, (i - 3) * 0.08) }),
	/** A letter deleted: a lower, shorter pop a step down. */
	del: (a: Audio, i = 0) => pop(a, step(Math.max(0, i - 1)) * 0.75, { gain: 0.32, ring: 0.4 }),
	/** Moving to a clue: a soft high pop, quieter than a key. */
	clue: (a: Audio) => pop(a, 1400, { gain: 0.22, ring: 0.5 }),
	/** An answer solved. */
	solved: (a: Audio) => chime(a),
	/** A Flow continuation: the chime, then a quick two-note flourish up. */
	flow: (a: Audio) => {
		chime(a);
		[19, 24].forEach((s, k) => pop(a, semi(s), { t: 0.2 + k * 0.06, gain: 0.3, ring: k === 1 ? 1.6 : 0.6, pan: 0.15 + k * 0.15 }));
	},
	/** A check that finds mistakes: the low wood thud. */
	wrong: (a: Audio) => pop(a, 170, { gain: 0.6, ring: 0.2, palette: WOOD }),
	/** The puzzle finished: a run up, a bright chord, a sparkle. */
	completed: (a: Audio) => {
		const run = [0, 2, 4, 5, 7, 9];
		run.forEach((i, k) => pop(a, step(i), { t: k * 0.055, gain: 0.42, ring: 0.6, pan: (k - 2.5) * 0.12 }));
		const t0 = run.length * 0.055 + 0.06;
		[0, 4, 7, 12].forEach((s, k) => pop(a, semi(s + 12), { t: t0 + k * 0.018, gain: 0.42, ring: 3.2, pan: (k - 1.5) * 0.3 }));
		for (let k = 0; k < 7; k++) {
			const note = semi(24 + PENTA[k % 5]! + (k > 4 ? 12 : 0));
			pop(a, note, { t: t0 + 0.25 + k * 0.07 + Math.random() * 0.03, gain: 0.16, ring: 1.4, pan: Math.random() * 1.2 - 0.6 });
		}
	},
} satisfies Record<string, (a: Audio, i?: number) => void>;

export type SoundKind = keyof typeof EVENTS;
export const SOUND_KINDS = Object.keys(EVENTS) as SoundKind[];

/** Play one named sound (`index`: the cell's place in the answer, for 'key' and 'del'). */
export function sound(kind: SoundKind, index?: number): void {
	try {
		if (!enabled()) return;
		const a = ac();
		if (a) EVENTS[kind](a, index);
	} catch {
		// Never let a sound break play.
	}
}

/** The pitch for a surface of this size: bigger is lower (√area keeps the steps even). */
export function uiPitch(width: number, height: number): number {
	const s = Math.sqrt(Math.max(1, width * height));
	return Math.max(260, Math.min(1700, 1700 * (44 / s) ** 0.95));
}

/**
 * A subtle UI pop, pitched by the element's size (a tab is higher than a sheet). Only once
 * a gesture has woken the audio: something that opens by itself on load stays quiet.
 */
export function uiPop(el: Element | null | undefined): void {
	try {
		if (!el || !audio || !enabled()) return;
		const a = ac();
		if (!a) return;
		const r = el.getBoundingClientRect();
		if (!r.width || !r.height) return;
		pop(a, uiPitch(r.width, r.height), { gain: 0.3, ring: Math.sqrt(r.width * r.height) > 120 ? 1.8 : 1 });
	} catch {
		// Never let a sound break the UI.
	}
}

/** Tests: forget the context (and whether Web Audio was missing). */
export function resetSound(): void {
	audio = null;
	unavailable = false;
}
